/* ============================================================
   TL PLAYER — minimal video controls
   Markup: snippets/tl-video.liquid   Styles: assets/tl-player.css
   Added Aug 2026.

   *** THE ENHANCEMENT ORDER IS THE SAFETY DESIGN. ***
   The markup ships with the NATIVE controls attribute present and the
   custom UI hidden in CSS. This script removes the native attribute
   and adds .is-enhanced, which reveals the custom UI.

   Done that way round, a script that fails to load, is blocked, or
   throws leaves an ordinary working native player. Doing it the other
   way — markup with no controls, JS adding them — would mean any script
   failure produces a video that cannot be played at all, on a page
   whose whole job is to be watched.

   If you are editing this file: keep the removeAttribute and the class
   together at the top of enhance(), after the elements are confirmed
   present. Never move them earlier.

   NO DEPENDENCIES, and no build step. It runs on DOMContentLoaded and
   again on load, because a video inside a lazily-rendered section can
   arrive late; players already wired are skipped via a data flag.
   ============================================================ */

(function () {
  'use strict';

  var SVG_PLAY  = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5.5v13l11-6.5z"/></svg>';
  var SVG_PAUSE = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 5h3.4v14H7zM13.6 5H17v14h-3.4z"/></svg>';
  var SVG_SOUND = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 9.5v5h3.2L12 19V5L7.2 9.5zM15.4 8.6a4.6 4.6 0 0 1 0 6.8l1.3 1.3a6.4 6.4 0 0 0 0-9.4z"/></svg>';
  var SVG_MUTED = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 9.5v5h3.2L12 19V5L7.2 9.5zM19.8 12l1.9-1.9-1.1-1.1-1.9 1.9-1.9-1.9-1.1 1.1 1.9 1.9-1.9 1.9 1.1 1.1 1.9-1.9 1.9 1.9 1.1-1.1z"/></svg>';
  var SVG_FULL  = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 9V4h5v2H6v3zm11-5h5v5h-2V6h-3zM6 15v3h3v2H4v-5zm12 0h2v5h-5v-2h3z"/></svg>';

  function fmt(t) {
    if (!isFinite(t) || t < 0) t = 0;
    var m = Math.floor(t / 60);
    var s = Math.floor(t % 60);
    return m + ':' + (s < 10 ? '0' : '') + s;
  }

  function enhance(root) {
    if (root.dataset.tlReady) return;

    var video = root.querySelector('video');
    var bigBtn = root.querySelector('[data-tl-big]');
    var playBtn = root.querySelector('[data-tl-play]');
    var muteBtn = root.querySelector('[data-tl-mute]');
    var fsBtn = root.querySelector('[data-tl-fs]');
    var track = root.querySelector('[data-tl-progress]');
    var fill = root.querySelector('[data-tl-fill]');
    var time = root.querySelector('[data-tl-time]');

    /* Bail out with the native player intact if anything is missing,
       rather than half-wiring a broken custom bar. */
    if (!video || !bigBtn || !playBtn || !track || !fill) return;

    root.dataset.tlReady = '1';

    /* THE SWAP. Native controls off, custom UI on — only now that every
       element is confirmed present. See the note at the top. */
    video.removeAttribute('controls');
    root.classList.add('is-enhanced');

    function setPlaying(playing) {
      root.classList.toggle('is-playing', playing);
      playBtn.innerHTML = playing ? SVG_PAUSE : SVG_PLAY;
      playBtn.setAttribute('aria-label', playing ? 'Pause' : 'Play');
      bigBtn.setAttribute('aria-label', playing ? 'Pause video' : 'Play video');
    }

    function toggle() {
      if (video.paused) {
        var p = video.play();
        if (p && p.catch) p.catch(function () {});
      } else {
        video.pause();
      }
    }

    bigBtn.addEventListener('click', toggle);
    playBtn.addEventListener('click', toggle);

    video.addEventListener('play', function () { setPlaying(true); });
    video.addEventListener('playing', function () { setPlaying(true); });
    video.addEventListener('pause', function () { setPlaying(false); });
    video.addEventListener('ended', function () { setPlaying(false); });
    setPlaying(!video.paused);

    function paint() {
      var d = video.duration;
      var pct = (isFinite(d) && d > 0) ? (video.currentTime / d) * 100 : 0;
      fill.style.width = pct + '%';
      track.setAttribute('aria-valuenow', Math.round(pct));
      if (time) time.textContent = fmt(video.currentTime) + ' / ' + fmt(d);
    }
    video.addEventListener('timeupdate', paint);
    video.addEventListener('loadedmetadata', paint);
    paint();

    /* Seeking. Pointer rather than click so a drag scrubs, and the
       capture keeps events coming if the pointer leaves the track. */
    function seekTo(clientX) {
      var r = track.getBoundingClientRect();
      if (!r.width) return;
      var ratio = (clientX - r.left) / r.width;
      ratio = Math.min(1, Math.max(0, ratio));
      if (isFinite(video.duration)) video.currentTime = ratio * video.duration;
    }

    var scrubbing = false;
    track.addEventListener('pointerdown', function (e) {
      scrubbing = true;
      if (track.setPointerCapture) {
        try { track.setPointerCapture(e.pointerId); } catch (err) {}
      }
      seekTo(e.clientX);
    });
    track.addEventListener('pointermove', function (e) {
      if (scrubbing) seekTo(e.clientX);
    });
    track.addEventListener('pointerup', function () { scrubbing = false; });
    track.addEventListener('pointercancel', function () { scrubbing = false; });

    /* Keyboard seeking — the track is focusable and exposed as a slider,
       so arrow keys must move it or it is a control nobody can use. */
    track.addEventListener('keydown', function (e) {
      if (!isFinite(video.duration)) return;
      var step = video.duration / 20;
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        video.currentTime = Math.min(video.duration, video.currentTime + step);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        video.currentTime = Math.max(0, video.currentTime - step);
      }
    });

    if (muteBtn) {
      function paintMute() {
        muteBtn.innerHTML = video.muted ? SVG_MUTED : SVG_SOUND;
        muteBtn.setAttribute('aria-label', video.muted ? 'Unmute' : 'Mute');
      }
      muteBtn.addEventListener('click', function () {
        video.muted = !video.muted;
        paintMute();
      });
      video.addEventListener('volumechange', paintMute);
      paintMute();
    }

    if (fsBtn) {
      fsBtn.innerHTML = SVG_FULL;
      fsBtn.addEventListener('click', function () {
        /* webkitEnterFullscreen is the iPhone path: Safari on iOS will
           not put an arbitrary element fullscreen, only the video. */
        var el = root;
        if (document.fullscreenElement || document.webkitFullscreenElement) {
          (document.exitFullscreen || document.webkitExitFullscreen || function () {}).call(document);
          return;
        }
        var req = el.requestFullscreen || el.webkitRequestFullscreen;
        if (req) {
          try { req.call(el); return; } catch (err) {}
        }
        if (video.webkitEnterFullscreen) {
          try { video.webkitEnterFullscreen(); } catch (err) {}
        }
      });
    }
  }

  function run() {
    var players = document.querySelectorAll('[data-tl-player]');
    for (var i = 0; i < players.length; i++) enhance(players[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
  window.addEventListener('load', run);

  /* The theme editor re-renders a section on every settings change,
     which replaces the markup and loses the wiring. */
  document.addEventListener('shopify:section:load', run);
})();
